import React, { Component, ReactNode } from 'react';
import FormattedProductPrice from '~/FormattedProductPrice';
import { Item as ProductItem, Product } from '~/Api/Product';
import { TruncatedText } from '~/libs/ui/atoms/TruncatedText';
import PricedProduct from '~/Product/PricedProduct';
import ProductPriceInfo from '~/ProductPriceInfo';
import ProductStatusIcon from '~/ProductStatusIcon';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';

import styles from './styles.less';

const TOOLTIP_WIDTH_CLASS = 'w-max max-w-xs';

interface CellTextProps {
	text: string | null;
	to: { pathname: string; search: string };
	testId: string;
}

function CellText({ text, to, testId }: CellTextProps) {
	const value = text ?? '';

	// Keep the detail link when the field is empty. TruncatedText renders nothing for
	// empty text, and a row click lands on whichever cell is in the middle.
	if (value === '') {
		return <Link data-testid={testId} styles={{ button: styles.cellContentWithTooltip }} to={to} />;
	}

	return (
		<TruncatedText
			text={value}
			tooltipWidthClass={TOOLTIP_WIDTH_CLASS}
			portal
			renderReference={(reference) => (
				<Link data-testid={testId} styles={{ button: styles.cellContentWithTooltip }} to={to}>
					{reference}
				</Link>
			)}
		/>
	);
}

interface Props {
	index: number;
	pricedProduct: PricedProduct<ProductItem>;
	highlightProductId: string | null;
	priceType: string | null;
	accessory?(product: PricedProduct<ProductItem>): ReactNode;
	detailLink(product: Product): { pathname: string; search: string };
	packageCell?(product: PricedProduct<ProductItem>, index: number): ReactNode;
	isFetching: boolean;
	style: any;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	shouldComponentUpdate(nextProps: Props) {
		return (
			this.props.index !== nextProps.index ||
			this.props.pricedProduct !== nextProps.pricedProduct ||
			this.props.highlightProductId !== nextProps.highlightProductId ||
			this.props.priceType !== nextProps.priceType ||
			this.props.accessory !== nextProps.accessory ||
			this.props.detailLink !== nextProps.detailLink ||
			this.props.isFetching !== nextProps.isFetching ||
			this.props.style !== nextProps.style ||
			this.props.checked !== nextProps.checked
		);
	}

	renderPrice(pricedProduct: PricedProduct<ProductItem>, detailLinkString: { pathname: string; search: string }) {
		const { priceType } = this.props;
		const { product, price } = pricedProduct;
		const priceInfo = (
			<div className={styles.ellipsis}>
				<ProductPriceInfo price={price} priceType={priceType} product={product} />
			</div>
		);

		const condensedType = price?.humanCondensedFormatType() ?? '';

		if (price === null || condensedType === '') {
			return (
				<Link styles={{ button: styles.priceTypeCellContentWithTooltip }} to={detailLinkString}>
					{priceInfo}
				</Link>
			);
		}

		return (
			<TruncatedText
				text={condensedType}
				tooltip={price.humanFormatType()}
				tooltipPosition='left'
				tooltipWidthClass={TOOLTIP_WIDTH_CLASS}
				portal
				renderReference={(reference) => (
					<Link styles={{ button: styles.priceTypeCellContentWithTooltip }} to={detailLinkString}>
						{reference}
						{priceInfo}
					</Link>
				)}
			/>
		);
	}

	render() {
		const {
			style,
			index,
			pricedProduct,
			highlightProductId,
			accessory,
			detailLink,
			packageCell,
			isFetching,
			checked,
			checkRow,
		} = this.props;
		const { product } = pricedProduct;
		const detailLinkString = detailLink(product);

		return (
			<div
				className={
					product.status.isRemoved()
						? styles.removedRow
						: product.id === highlightProductId
						? styles.highlightedRow
						: styles.row
				}
				data-productid={product.id}
				data-testid='product-table-row'
				key={product.id}
				style={style}
			>
				{accessory !== undefined ? (
					<div className={styles.addOrRemoveCell}>{accessory(pricedProduct)}</div>
				) : (
					<div className={styles.addOrRemoveCell} data-testid='delete-product'>
						<Checkbox checked={checked} onChange={checkRow} />
					</div>
				)}
				<div className={styles.countryCell}>
					{product.dataset.source.shortName ? (
						<TruncatedText
							text={product.dataset.source.shortName}
							tooltip={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
							className={styles.datasetSourceShortNameWithTooltip}
							tooltipPosition='right'
							tooltipWidthClass={TOOLTIP_WIDTH_CLASS}
							portal
							renderReference={(reference) => (
								<Link styles={{ button: styles.countryCellContent }} to={detailLinkString}>
									<div className={styles.countryIcon}>
										<CountryIcon
											code={product.dataset.source.country.alpha2}
											name={product.dataset.source.country.name}
										/>
									</div>
									{product.dataset.source.country.alpha2}
									{reference}
								</Link>
							)}
						/>
					) : (
						<Link styles={{ button: styles.countryCellContent }} to={detailLinkString}>
							<div className={styles.countryIcon}>
								<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
							</div>
							{product.dataset.source.country.alpha2}
						</Link>
					)}
				</div>
				<div className={styles.brandCell}>
					{product.uppercasedBrandName ? (
						<TruncatedText
							text={product.uppercasedBrandName}
							truncateClassName='line-clamp-2 whitespace-normal'
							tooltipWidthClass={TOOLTIP_WIDTH_CLASS}
							portal
							renderReference={(reference) => (
								<Link
									data-testid='product-table-cell-brand'
									styles={{ button: styles.brandCellContentWithTooltip }}
									to={detailLinkString}
								>
									{reference}
								</Link>
							)}
						/>
					) : (
						<Link
							data-testid='product-table-cell-brand'
							styles={{ button: styles.brandCellContentWithTooltip }}
							to={detailLinkString}
						/>
					)}
				</div>
				<div className={styles.mahCell}>
					<CellText
						text={product.uppercasedMarketingAuthorizationHolder}
						testId='product-table-cell-marketing-authorization-holder'
						to={detailLinkString}
					/>
				</div>
				<div className={styles.atcCell}>
					<CellText text={product.atc} testId='product-table-cell-atc' to={detailLinkString} />
				</div>
				<div className={styles.activeSubstanceCell}>
					<CellText
						text={product.uppercasedActiveSubstance}
						testId='product-table-cell-active-substance'
						to={detailLinkString}
					/>
				</div>
				<div className={styles.dosageCell}>
					<CellText text={product.uppercasedDosageForm} testId='product-table-cell-dosage-form' to={detailLinkString} />
				</div>
				<div className={styles.dosageStrengthCell}>
					<CellText text={product.dosageStrength} testId='product-table-cell-dosage-strength' to={detailLinkString} />
				</div>
				<div className={styles.packageCell}>
					{packageCell?.(pricedProduct, index) ?? (
						<CellText text={product.packageSize} testId='product-table-cell-package-size' to={detailLinkString} />
					)}
				</div>
				<div className={styles.priceCell}>
					<Link styles={{ button: styles.priceCellContent }} to={detailLinkString}>
						<FormattedProductPrice
							rounded
							dataSource={product.dataset.source}
							isFetching={isFetching}
							productPrice={pricedProduct.price}
						/>
					</Link>
				</div>
				<div className={styles.priceTypeCell}>{this.renderPrice(pricedProduct, detailLinkString)}</div>
				<div className={styles.detailCell}>
					<Link to={detailLinkString}>
						<ProductStatusIcon status={product.status} />
					</Link>
				</div>
			</div>
		);
	}
}
